import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, Target, Percent, ChevronLeft, Loader2, Swords, UserPlus, UserCheck, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatLevel } from "@/lib/skillLevels";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { LevelEvolutionChart } from "./LevelEvolutionChart";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { MatchHistoryDrawer } from "./MatchHistoryDrawer";
import { EventAchievementsSection } from "./EventAchievementsSection";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface PlayerPreviewProps {
  playerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlayerPreviewModal({ playerId, open, onOpenChange }: PlayerPreviewProps) {
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  const [matchDrawerOpen, setMatchDrawerOpen] = useState(false);
  const { toast } = useToast();

  const { data: currentUser } = useQuery<any>({
    queryKey: ["/api/auth/user"],
  });

  const { data: playerData, isLoading } = useQuery<any>({
    queryKey: [`/api/players/${playerId}`],
    enabled: !!playerId && open,
  });

  const { data: friends } = useQuery<any[]>({
    queryKey: ["/api/friends"],
    enabled: !!playerId && open,
  });

  const { data: sentRequests } = useQuery<any[]>({
    queryKey: ["/api/friends/sent-pending"],
    enabled: !!playerId && open,
  });

  const { data: receivedRequests } = useQuery<any[]>({
    queryKey: ["/api/friends/requests"],
    enabled: !!playerId && open,
  });

  const addFriendMutation = useMutation({
    mutationFn: async (addresseeId: string) => {
      const res = await apiRequest("POST", "/api/friends/request", { addresseeId });
      if (!res.ok) throw new Error("Error al enviar solicitud de amistad");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/sent-pending"] });
      toast({
        title: "Solicitud enviada",
        description: "Se ha enviado la solicitud de amistad correctamente.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isMe = currentUser?.id === playerId;
  const isFriend = friends?.some(f => f.id === playerId);
  const isPendingSent = sentRequests?.some(r => r.addresseeId === playerId && r.status === "pending");
  const isPendingReceived = receivedRequests?.some(r => r.requesterId === playerId && r.status === "pending");

  const { data: levelHistory } = useQuery<any[]>({
    queryKey: [`/api/profile/level-history`, { userId: playerId }],
    queryFn: async () => {
      const res = await fetch(`/api/profile/level-history?userId=${playerId}`);
      if (!res.ok) throw new Error("Failed to fetch level history");
      return res.json();
    },
    enabled: !!playerId && open,
  });

  const { data: matchHistory } = useQuery<any[]>({
    queryKey: [`/api/profile/match-history`, { userId: playerId }],
    queryFn: async () => {
      const res = await fetch(`/api/profile/match-history?userId=${playerId}`);
      if (!res.ok) throw new Error("Failed to fetch match history");
      return res.json();
    },
    enabled: !!playerId && open,
  });

  if (!playerId) return null;

  const user = playerData?.user;
  const profile = playerData?.profile;
  const name = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() : "Jugador";
  
  const initials = name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "??";

  const winRate = profile?.matchesPlayed > 0 
    ? Math.round((profile.matchesWon / profile.matchesPlayed) * 100) 
    : 0;

  // Sync currentLevel with the most recent entry from level history if available
  const currentLevelFromHistory = levelHistory && levelHistory.length > 0 
    ? levelHistory[0].newLevel 
    : (profile?.skillLevel || 3.0);

  // Get last 5 matches and enrich with elo change data
  const recentMatches = (matchHistory?.slice(0, 5) || []).map((match: any) => {
    // For league matches, we extract the rating delta from ratingChanges
    let eloDelta = null;
    if (match.leagueId && match.ratingChanges) {
      // Find the player in the ratingChanges object
      const playerKey = Object.keys(match.ratingChanges).find(key => 
        (key === 'team1Player1' && match.team1Player1Id === playerId) ||
        (key === 'team1Player2' && match.team1Player2Id === playerId) ||
        (key === 'team2Player1' && match.team2Player1Id === playerId) ||
        (key === 'team2Player2' && match.team2Player2Id === playerId)
      );
      
      if (playerKey && match.ratingChanges[playerKey]) {
        eloDelta = match.ratingChanges[playerKey].delta;
      }
    }
    
    return {
      ...match,
      eloDelta,
    };
  });

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        hideCloseButton
        className="max-w-none w-full h-full p-0 border-none rounded-none bg-background flex flex-col sm:max-w-2xl sm:h-[90vh] sm:rounded-xl sm:border sm:overflow-hidden overflow-hidden"
      >
        <DialogHeader 
          className="p-4 border-b flex flex-row items-center gap-4 shrink-0 space-y-0 relative"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
        >
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-testid="button-back"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <DialogTitle className="text-xl font-bold">Perfil del Jugador</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 w-full">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-12 gap-4 h-[60vh]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground animate-pulse">Cargando perfil...</p>
            </div>
          ) : !user ? (
            <div className="p-12 text-center h-[60vh] flex items-center justify-center">
              <p className="text-muted-foreground">No se encontró el perfil del jugador.</p>
            </div>
          ) : (
            <div className="w-full max-w-full overflow-x-hidden p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Header Profile */}
              <div className="flex flex-col items-center text-center px-2">
                <Avatar className="h-24 w-24 border-4 border-background shadow-lg mb-4">
                  <AvatarImage src={user.profileImageUrl} alt={name} />
                  <AvatarFallback className="text-2xl font-bold bg-primary/10 text-primary">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-2xl font-bold break-words w-full px-4">{name}</h2>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                  <Badge variant="secondary" className="px-3 py-1 bg-primary/10 text-primary border-none whitespace-nowrap">
                    Nivel {formatLevel(currentLevelFromHistory)}
                  </Badge>
                  <Badge variant="outline" className="px-3 py-1 whitespace-nowrap">
                    Pádel
                  </Badge>
                </div>

                {!isMe && (
                  <div className="mt-4">
                    {isFriend ? (
                      <Button variant="outline" className="gap-2 text-green-600 border-green-200 bg-green-50 hover:bg-green-100" disabled>
                        <UserCheck className="h-4 w-4" />
                        Amigos
                      </Button>
                    ) : isPendingSent ? (
                      <Button variant="outline" className="gap-2" disabled>
                        <Clock className="h-4 w-4" />
                        Solicitud Enviada
                      </Button>
                    ) : isPendingReceived ? (
                      <Button variant="outline" className="gap-2" disabled>
                        <Clock className="h-4 w-4" />
                        Solicitud Pendiente
                      </Button>
                    ) : (
                      <Button 
                        onClick={() => playerId && addFriendMutation.mutate(playerId)}
                        disabled={addFriendMutation.isPending}
                        className="gap-2"
                      >
                        {addFriendMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <UserPlus className="h-4 w-4" />
                        )}
                        Añadir Amigo
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Level Evolution Chart */}
              <Card className="mx-0 sm:mx-0 overflow-hidden">
                <CardContent className="pt-6 px-4 sm:px-6">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      Evolución de Nivel
                    </span>
                  </div>
                  <div className="h-[280px] w-full overflow-hidden">
                    <LevelEvolutionChart 
                      currentLevel={profile?.skillLevel || 3.0} 
                      userId={playerId}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 px-1">
                <div className="p-4 rounded-xl bg-muted/50 flex flex-col items-center text-center">
                  <Trophy className="h-5 w-5 text-amber-500 mb-2" />
                  <span className="text-xs text-muted-foreground uppercase font-bold tracking-tight">Victorias</span>
                  <span className="text-2xl font-black">{profile?.matchesWon ?? 0}</span>
                </div>
                <div className="p-4 rounded-xl bg-muted/50 flex flex-col items-center text-center">
                  <Percent className="h-5 w-5 text-green-500 mb-2" />
                  <span className="text-xs text-muted-foreground uppercase font-bold tracking-tight">% Victorias</span>
                  <span className="text-2xl font-black">{winRate}%</span>
                </div>
              </div>

              {/* Event achievements (Torneos Cerrados + Americanos) */}
              <EventAchievementsSection userId={playerId} />

              {/* Recent Matches */}
              <div className="space-y-4 px-1 pb-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 px-1">
                  <Swords className="h-4 w-4" />
                  Últimos Partidos
                </h3>
                <div className="space-y-3">
                  {recentMatches.length > 0 ? (
                    recentMatches.map((match: any) => {
                      const sets = match.score?.sets || [];
                      const team1Won = match.winner === 1;
                      
                      const getFirstName = (fullName: string | null | undefined) => {
                        if (!fullName) return '';
                        return fullName.split(' ')[0];
                      };
                      
                      const getInitial = (fullName: string | null | undefined) => {
                        if (!fullName) return '?';
                        return fullName.charAt(0).toUpperCase();
                      };
                      
                      const team1Player1 = { name: getFirstName(match.team1Player1Name), initial: getInitial(match.team1Player1Name), image: match.team1Player1Image };
                      const team1Player2 = match.team1Player2Name ? { name: getFirstName(match.team1Player2Name), initial: getInitial(match.team1Player2Name), image: match.team1Player2Image } : null;
                      const team2Player1 = { name: getFirstName(match.team2Player1Name), initial: getInitial(match.team2Player1Name), image: match.team2Player1Image };
                      const team2Player2 = match.team2Player2Name ? { name: getFirstName(match.team2Player2Name), initial: getInitial(match.team2Player2Name), image: match.team2Player2Image } : null;
                      
                      const winnerPlayers = team1Won ? [team1Player1, team1Player2] : [team2Player1, team2Player2];
                      const loserPlayers = team1Won ? [team2Player1, team2Player2] : [team1Player1, team1Player2];
                      const winnerSets = sets.map((s: any) => team1Won ? s.team1 : s.team2);
                      const loserSets = sets.map((s: any) => team1Won ? s.team2 : s.team1);

                      // Get Elo changes
                      let winnerElo: number | null = null;
                      let loserElo: number | null = null;
                      if (match.ratingChanges) {
                        const rc = match.ratingChanges;
                        if (team1Won) {
                          winnerElo = rc.team1Player1?.delta || rc.team1Player2?.delta || null;
                          loserElo = rc.team2Player1?.delta || rc.team2Player2?.delta || null;
                        } else {
                          winnerElo = rc.team2Player1?.delta || rc.team2Player2?.delta || null;
                          loserElo = rc.team1Player1?.delta || rc.team1Player2?.delta || null;
                        }
                      }
                      
                      return (
                        <Card 
                          key={match.id} 
                          className="border transition-all hover-elevate p-3 bg-card overflow-hidden cursor-pointer"
                          onClick={() => {
                            setSelectedMatch(match);
                            setMatchDrawerOpen(true);
                          }}
                        >
                          {/* Header: Date + League Badge */}
                          <div className="flex items-center justify-between mb-2 gap-2">
                            <span
                              className="text-[10px] text-muted-foreground truncate min-w-0"
                              data-testid={`text-player-match-date-club-${match.id}`}
                            >
                              {format(new Date(match.playedAt), "d MMM", { locale: es })}
                              {match.homeClubName ? ` · ${match.homeClubName}` : ""}
                            </span>
                            <Badge variant="outline" className="text-[10px] font-medium px-1.5 py-0 h-5 truncate max-w-[100px] shrink-0">
                              {match.leagueName || "Global"}
                            </Badge>
                          </div>
                          
                          {/* Winner Row */}
                          <div className="border-l-4 border-l-victory bg-victory/5 rounded-r-md px-2 py-1.5 mb-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                <Trophy className="h-3.5 w-3.5 text-victory flex-shrink-0" />
                                <div className="flex items-center gap-1 min-w-0">
                                  {winnerPlayers.filter(Boolean).map((player, idx) => (
                                    <div key={idx} className="flex items-center gap-0.5">
                                      <Avatar className="h-5 w-5 border border-victory/30">
                                        {player?.image && <AvatarImage src={player.image} alt={player?.name || ''} />}
                                        <AvatarFallback className="text-[8px] font-bold bg-victory/10 text-victory">
                                          {player?.initial}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span className="text-[11px] font-medium truncate max-w-[50px]">{player?.name}</span>
                                      {idx === 0 && winnerPlayers[1] && <span className="text-[10px] text-muted-foreground mx-0.5">/</span>}
                                    </div>
                                  ))}
                                </div>
                                {winnerElo !== null && (
                                  <span className={cn(
                                    "text-[10px] font-bold ml-1",
                                    winnerElo >= 0 ? "text-victory" : "text-destructive"
                                  )}>
                                    {winnerElo >= 0 ? `+${winnerElo}` : winnerElo}
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-0.5 flex-shrink-0">
                                {winnerSets.map((score: number, idx: number) => (
                                  <span key={idx} className="text-sm font-bold w-4 text-center">{score}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          
                          {/* Loser Row */}
                          <div className="border-l-4 border-l-muted px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 flex-1 min-w-0 pl-4">
                                <div className="flex items-center gap-1 min-w-0">
                                  {loserPlayers.filter(Boolean).map((player, idx) => (
                                    <div key={idx} className="flex items-center gap-0.5">
                                      <Avatar className="h-5 w-5 border border-muted">
                                        {player?.image && <AvatarImage src={player.image} alt={player?.name || ''} />}
                                        <AvatarFallback className="text-[8px] font-medium bg-muted text-muted-foreground">
                                          {player?.initial}
                                        </AvatarFallback>
                                      </Avatar>
                                      <span className="text-[11px] text-muted-foreground truncate max-w-[50px]">{player?.name}</span>
                                      {idx === 0 && loserPlayers[1] && <span className="text-[10px] text-muted-foreground mx-0.5">/</span>}
                                    </div>
                                  ))}
                                </div>
                                {loserElo !== null && (
                                  <span className={cn(
                                    "text-[10px] font-bold ml-1",
                                    loserElo >= 0 ? "text-victory" : "text-destructive"
                                  )}>
                                    {loserElo >= 0 ? `+${loserElo}` : loserElo}
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-0.5 flex-shrink-0">
                                {loserSets.map((score: number, idx: number) => (
                                  <span key={idx} className="text-sm font-medium text-muted-foreground w-4 text-center">{score}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground italic px-1">No hay partidos recientes registrados.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
    
    <MatchHistoryDrawer
      open={matchDrawerOpen}
      onOpenChange={(open) => {
        setMatchDrawerOpen(open);
        if (!open) setSelectedMatch(null);
      }}
      match={selectedMatch}
      viewerName={name}
      isViewerParticipant={isMe}
      currentUserId={currentUser?.id}
    />
    </>
  );
}